import React, { useState, useEffect, useMemo } from "react";
import Table from "../../../../components/TableV2";
import TitleButtons from "../../../../components/TitleButtons";
import { generatePdfReport } from "../../../../utils/pdf/generatePdfReport";
import numeroParaExtenso from "../../../../utils/numeroPorExtenso";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ptBR } from "date-fns/locale";
import { useAjustesSuprimentos } from "./ajustesData";
import TableActions from "../../../../components/TableActions";

registerLocale("pt-BR", ptBR);

const SuprimentosEstoqueAjustes: React.FC = () => {
  const headers = [
    "Responsável",
    "Código",
    "Nome do Produto",
    "Custo Unitário",
    "Quantidade",
    "Posição",
    "Data",
    "Hora",
  ];

  const columnWidths = [
    "140px", // Responsável
    "90px", // Código
    "360px", // Nome
    "120px", // Custo
    "90px", // Qtd
    "80px", // Posição
    "100px", // Data
    "80px", // Hora
  ];

  // Carrega ajustes do backend
  const { rows, loading, refresh } = useAjustesSuprimentos();

  const [selectedColumns, setSelectedColumns] = useState<boolean[]>(
    headers.map(() => true)
  );
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateOption, setDateOption] = useState<
    "diario" | "mensal" | "custom" | null
  >(null);

  // Filtra as linhas por data (coluna 6 = "Data")
  const filteredRows = useMemo(() => {
    if (!startDate || !endDate) return rows;
    const start = new Date(startDate.setHours(0, 0, 0, 0)).getTime();
    const end = new Date(endDate.setHours(23, 59, 59, 999)).getTime();

    return rows.filter((r) => {
      const dateStr = r[6] as string; // "dd/MM/yyyy"
      const [day, month, year] = dateStr.split("/").map((x) => parseInt(x, 10));
      const rowDate = new Date(year, month - 1, day).getTime();
      return rowDate >= start && rowDate <= end;
    });
  }, [rows, startDate, endDate]);

  const enabledColumnIndexes = useMemo(
    () =>
      selectedColumns
        .map((checked, i) => (checked ? i : -1))
        .filter((i) => i !== -1),
    [selectedColumns]
  );

  // Índices: custo=3, quantidade=4
  const totalProdutos = useMemo(
    () =>
      filteredRows.reduce((acc, r) => {
        const qtd = parseInt(r[4] as string, 10);
        return acc + (Number.isFinite(qtd) ? qtd : 0);
      }, 0),
    [filteredRows]
  );

  const valorTotal = useMemo(() => {
    return filteredRows.reduce((acc, r) => {
      const custo = parseFloat(
        String(r[3])
          .replace(/[^\d,]/g, "")
          .replace(",", ".")
      );
      const qtd = parseInt(r[4] as string, 10);
      const inc =
        Number.isFinite(custo) && Number.isFinite(qtd) ? custo * qtd : 0;
      return acc + inc;
    }, 0);
  }, [filteredRows]);

  const handleColumnCheckboxChange = (index: number, checked: boolean) => {
    const updated = [...selectedColumns];
    updated[index] = checked;
    setSelectedColumns(updated);
  };

  // Datas existentes (para destacar no calendário)
  const uniqueDates = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => set.add(r[6] as string));
    return Array.from(set);
  }, [rows]);

  const isValidDate = (date: Date) => {
    const s = `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}`;
    return uniqueDates.includes(s);
  };

  const handleDateOptionChange = (option: "diario" | "mensal") => {
    setDateOption(option);
    const today = new Date();
    if (option === "diario") {
      today.setHours(0, 0, 0, 0);
      setStartDate(today);
      setEndDate(today);
    } else if (option === "mensal") {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      setStartDate(firstDay);
      setEndDate(lastDay);
    }
  };

  return (
    <div>
      <TitleButtons
        fullBleedLine
        title="Suprimentos - Ajustes no Estoque de Serviço & Comercial"
      />
      <TableActions
        buttons={[
          {
            label: loading ? "Carregando..." : "Gerar Relatório",
            onClick: () =>
              generatePdfReport({
                reportTitle: "Relatório de Ajustes de Estoque - Suprimentos",
                fileNamePrefix: "Suprimentos-Estoque-Ajustes",
                subtitle: "Informações dos Ajustes de Estoque",
                headers,
                rows: filteredRows,
                custoColIndex: 3, // "Custo Unitário"
                enabledColumns: enabledColumnIndexes,
                footerLines: [
                  `Total de Itens Ajustados: ${totalProdutos}`,
                  `Valor Total dos Ajustes: R$ ${valorTotal.toLocaleString(
                    "pt-BR",
                    { minimumFractionDigits: 2 }
                  )} (${numeroParaExtenso(Math.floor(valorTotal))} reais)`,
                ],
              }),
            variant: "secondary",
            dropdownOptions: [
              {
                label: "Período e Colunas",
                checked: false,
                render: (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    <span
                      style={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: 14,
                      }}
                    >
                      Informe o Período:
                    </span>
                    <div style={{ marginBottom: 4 }}>
                      <DatePicker
                        selected={startDate}
                        onChange={(d: Date | null) => setStartDate(d)}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Data inicial"
                        locale="pt-BR"
                        dayClassName={(date) =>
                          isValidDate(date) ? "valid-date" : ""
                        }
                      />
                      <DatePicker
                        selected={endDate}
                        onChange={(d: Date | null) => setEndDate(d)}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Data final"
                        locale="pt-BR"
                        dayClassName={(date) =>
                          isValidDate(date) ? "valid-date" : ""
                        }
                      />
                      <style>{`.valid-date{background:#c4e7c3!important;border-radius:50%!important;}`}</style>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        fontFamily: "Poppins, sans-serif",
                        fontSize: 12,
                      }}
                    >
                      <label>
                        <input
                          type="checkbox"
                          checked={dateOption === "diario"}
                          onChange={() => handleDateOptionChange("diario")}
                          style={{ marginRight: 5, accentColor: "#124280" }}
                        />
                        Diário
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={dateOption === "mensal"}
                          onChange={() => handleDateOptionChange("mensal")}
                          style={{ marginRight: 5, accentColor: "#124280" }}
                        />
                        Mensal
                      </label>
                    </div>

                    <span
                      style={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: 14,
                        marginTop: 8,
                      }}
                    >
                      Colunas a Exibir
                    </span>
                  </div>
                ),
              },
              ...headers.map((h, i) => ({
                label: h,
                checked: selectedColumns[i],
                render: (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontFamily: "Poppins, sans-serif",
                      fontSize: 12,
                    }}
                  >
                    <label>
                      <input
                        type="checkbox"
                        checked={selectedColumns[i]}
                        onChange={(e) =>
                          handleColumnCheckboxChange(i, e.target.checked)
                        }
                        style={{ accentColor: "#124280" }}
                      />
                      {h}
                    </label>
                  </div>
                ),
              })),
            ],
          },
        ]}
      />
      <Table
        headers={headers}
        rows={filteredRows}
        columnWidths={columnWidths}
        responsiveWidths={{
          1300: {
            1: "120px",
            2: "70px",
            3: "80px",
            4: "320px",
            5: "110px",
            6: "80px",
            7: "70px",
            8: "100px",
          },
        }}
      />
    </div>
  );
};

export default SuprimentosEstoqueAjustes;
